import { FixedLoop } from '@/core/loop';
import { freshSeed } from '@/core/rng';
import { GAME } from '@/config/game';
import { settings } from '@/config/settings';
import { Stage } from '@/render/stage';
import { AmbianceFilter } from '@/render/ambiance';
import { QualityMonitor } from '@/render/quality';
import { InputManager } from '@/input/input';
import type { InputState } from '@/input/types';
import { rotationFor } from '@/bosses/registry';
import { Collisions } from '@/combat/collisions';
import { FightStep } from '@/combat/fight-step';
import { AchievementTracker } from '@/run/achievements';
import { DeathFlow } from '@/run/death-flow';
import { Hangar } from '@/run/hangar';
import { HomeFlow } from '@/run/home-flow';
import { Rewards } from '@/run/rewards';
import { RunFlow } from '@/run/run-flow';
import { RunState } from '@/run/run-state';
import { SectorFlow } from '@/run/sector-flow';
import { ShopFlow } from '@/run/shop-flow';
import { WaveDirector } from '@/run/wave-director';
import { World } from '@/scene/world';
import { CalibrationScreen } from '@/ui/calibration-screen';
import { ConfirmDialog } from '@/ui/confirm-dialog';
import { FullscreenPrompt } from '@/ui/fullscreen-prompt';
import { GameOverScreen } from '@/ui/gameover';
import { Hud } from '@/ui/hud';
import { syncHud } from '@/ui/hud-sync';
import { RecordToasts } from '@/ui/record-toasts';
import { RunEndScreen } from '@/ui/run-end-screen';
import { SettingsScreen } from '@/ui/settings-screen';
import { TitleScreen } from '@/ui/title-screen';
import { TouchUi } from '@/ui/touch-ui';
import { UpgradeScreen } from '@/ui/upgrade-screen';
import { XboxHint } from '@/ui/xbox-hint';
import { music } from '@/audio/music';

/**
 * Composition root + frame loop. Main flow: a sector run — waves + boss
 * (gems + weapon crate) → portal → upgrade shop → next room … → FINALE →
 * victory (or ENDLESS: the rush never ends). Runs are deterministic per
 * seed (?seed= pins it). docs/01, docs/03; module map: docs/05.
 */
export class Game {
  private readonly loop = new FixedLoop(GAME.fixedStep, GAME.maxFrameTime);
  private readonly input = new InputManager();
  private readonly world: World;
  private readonly run: RunState;
  private readonly ambiance = new AmbianceFilter();

  private readonly hud = new Hud();
  private readonly settingsScreen = new SettingsScreen();
  private readonly calibration = new CalibrationScreen();
  private readonly confirm = new ConfirmDialog();
  private readonly fsPrompt = new FullscreenPrompt();
  private readonly shopScreen: UpgradeScreen;
  private readonly gameOver: GameOverScreen;
  private readonly runEnd: RunEndScreen;
  private readonly title: TitleScreen;
  private readonly touchUi: TouchUi;
  private readonly quality: QualityMonitor;

  private readonly flow: RunFlow;
  private readonly shop: ShopFlow;
  private readonly hangar: Hangar;
  private readonly death: DeathFlow;
  private readonly rewards: Rewards;
  private readonly fight: FightStep;
  private readonly sector: SectorFlow;
  private readonly achievements: AchievementTracker;
  private readonly home: HomeFlow;
  private readonly xboxHint = new XboxHint();

  private prevMoveX = 0;
  private prevMoveY = 0;
  /** Last seen canvas size — open screens relayout when it changes */
  private lastScreenW = 0;
  private lastScreenH = 0;

  private constructor(private readonly stage: Stage) {
    const urlSeed = new URLSearchParams(window.location.search).get('seed');
    const pinnedSeed = urlSeed ? Number(urlSeed) >>> 0 : null;
    const run = (this.run = new RunState(rotationFor, pinnedSeed ?? freshSeed()));
    const world = (this.world = new World(stage.world));
    const hud = this.hud;
    // Ambiance grade (M4) is OPT-IN via ?fx=1 — it blanked the world on
    // Xbox Edge even on WebGL2 (driver-side shader failure). Xbox is the
    // primary target: no filter unless explicitly requested for testing.
    const wantFx = new URLSearchParams(window.location.search).get('fx') === '1';
    const glCtx = (stage.app.renderer as unknown as { context?: { webGLVersion?: number } })
      .context;
    if (wantFx && glCtx?.webGLVersion === 2) {
      stage.world.filters = [this.ambiance.filter];
    }

    // Systems + flows. Screen callbacks resolve lazily (flows built below).
    const toasts = new RecordToasts(hud);
    const save = (): void => this.flow.save();
    this.achievements = new AchievementTracker(run, world.player, hud);
    this.rewards = new Rewards(world, run, hud, this.input, {
      bossKilled: () => this.achievements.onBossKilled(),
      sectorWon: () => this.sector.onFinaleWon(),
    });
    const waves = new WaveDirector(world, run, hud, () => this.gameOver.open);
    this.shopScreen = new UpgradeScreen({
      buy: (i) => this.shop.buyCard(i),
      hangar: (action) => this.hangar.act(action),
      toggleLock: (i) => this.shop.toggleLock(i),
      reroll: () => this.shop.reroll(),
      heal: () => this.shop.buyHeal(),
      buyArmor: () => this.shop.buyArmor(),
      useArmor: () => this.shop.useStoredArmor(),
      continueRun: () => {
        this.achievements.checkBuild(); // shop buys can complete a build feat
        this.hangar.settle(); // an unchosen swap-or-scrap crate pays its gems
        this.flow.continueRun();
      },
      openMenu: () => this.home.openPause(), // shop gear: pause → ⌂ MAIN MENU
    });
    this.gameOver = new GameOverScreen({
      resolve: (cards, weaponBurned, extraArmor, toMenu) =>
        this.death.resolve(cards, weaponBurned, extraArmor, toMenu),
    });
    this.runEnd = new RunEndScreen({
      primary: () => this.sector.primary(),
      menu: () => this.sector.menu(),
    });
    this.title = new TitleScreen({
      startRun: (slot, setup) => this.flow.startRun(slot, setup),
      continueGame: (slot) => this.flow.continueGame(slot),
      openSettings: () => this.settingsScreen.show(stage.width, stage.height),
      openCalibration: () => this.calibration.show(stage.width, stage.height),
    });
    this.shop = new ShopFlow(world, run, waves, this.shopScreen, hud, stage, save);
    this.home = new HomeFlow(run, {
      settings: this.settingsScreen, confirm: this.confirm, shop: this.shopScreen, title: this.title,
    }, stage, save);
    this.hangar = new Hangar(world, run, this.shop, hud, save);
    this.death = new DeathFlow(
      world, run, this.shop,
      { gameOver: this.gameOver, shop: this.shopScreen, title: this.title },
      hud, this.input, toasts, stage, save, () => this.sector.onDeath(),
    );
    this.flow = new RunFlow(
      world, run, waves, this.shop, this.shopScreen, this.title, toasts, pinnedSeed,
    );
    this.sector = new SectorFlow(
      world, run, this.flow, this.rewards, this.achievements,
      { runEnd: this.runEnd, shop: this.shopScreen, title: this.title }, hud, stage,
    );
    const collisions = new Collisions(world, run, this.rewards, (dmg) => this.death.hitPlayer(dmg));
    this.fight = new FightStep({
      world, run, waves, collisions, rewards: this.rewards, death: this.death,
      shop: this.shop, hangar: this.hangar, toasts, hud, view: stage,
    });

    this.touchUi = new TouchUi(this.input.touch);
    stage.ui.addChild(
      hud.view, this.touchUi.view, this.shopScreen.view, this.gameOver.view, this.runEnd.view,
      // Settings sits ABOVE the title — it opens FROM the title (docs/12 J1)
      this.title.view, this.settingsScreen.view, this.calibration.view,
      this.fsPrompt.view,
      this.confirm.view, // topmost: modal confirm overlays everything
    );
    this.quality = new QualityMonitor(stage.app);

    this.flow.loadArena(0); // silent backdrop behind the title
    this.input.attach(stage.app.canvas);

    this.flow.booted = true;
    this.title.show(stage.width, stage.height);
    if (!settings.fullscreenPrompted) {
      this.fsPrompt.show(stage.width, stage.height);
      this.fsPrompt.onDone = () => this.title.show(stage.width, stage.height);
    }
  }

  static async create(): Promise<Game> {
    const stage = await Stage.create();
    return new Game(stage);
  }

  /** Kill-switch for the world filters (shader compile insurance). */
  disableFilters(): void {
    this.stage.world.filters = [];
  }

  start(): void {
    this.stage.app.ticker.add((ticker) => {
      const alpha = this.loop.advance(ticker.deltaMS / 1000, (dt) => this.update(dt));
      this.quality.update(ticker.deltaMS);
      this.render(alpha);
    });
  }

  private update(dt: number): void {
    const player = this.world.player;
    const screen = this.world.camera.worldToScreen(
      player.x, player.y, this.stage.width, this.stage.height,
    );
    const input = this.input.poll(screen.x, screen.y);
    const edge = GAME.menuStickEdge;
    const moveXEdge = Math.abs(input.moveX) > edge && Math.abs(this.prevMoveX) <= edge
      ? Math.sign(input.moveX) : 0;
    this.prevMoveX = input.moveX;
    const moveYEdge = Math.abs(input.moveY) > edge && Math.abs(this.prevMoveY) <= edge
      ? Math.sign(input.moveY) : 0;
    this.prevMoveY = input.moveY;

    this.xboxHint.tick(this.title.open, this.confirm, this.stage.width, this.stage.height);
    if (this.overlayInput(input, dt, moveXEdge, moveYEdge)) return;
    this.fight.step(dt, input, this.input.activeDevice);
    this.sector.tick(dt); // finale won → victory screen after the blast
  }

  /** Open overlays own the input (topmost first). @returns true = consumed */
  private overlayInput(
    input: InputState, dt: number, moveXEdge: number, moveYEdge: number,
  ): boolean {
    if (this.confirm.handleInput(input)) return true;
    if (this.calibration.handleInput(input)) return true;
    if (this.fsPrompt.handleInput(input)) return true;
    if (this.title.open) {
      if (!this.settingsScreen.handleInput(input)) this.title.handleInput(input);
      return true;
    }
    if (this.runEnd.open) {
      if (moveXEdge !== 0) this.runEnd.moveSelection(moveXEdge);
      if (moveYEdge !== 0) this.runEnd.moveSelection(moveYEdge);
      if (input.dashPressed) this.runEnd.activateSelected();
      return true;
    }
    if (this.gameOver.open) {
      this.gameOver.update(dt); // burn animation keeps running
      if (moveXEdge !== 0) this.gameOver.moveSelection(moveXEdge);
      if (moveYEdge !== 0) this.gameOver.moveSelection(moveYEdge);
      if (input.dashPressed) this.gameOver.activateSelected();
      return true;
    }
    // Pause (fight AND shop break): settings + ⌂ MAIN MENU (run/home-flow.ts)
    if (this.settingsScreen.handleInput(input)) return true;
    if (input.pausePressed) {
      this.home.openPause();
      return true;
    }
    if (this.shopScreen.open) {
      this.shopScreen.tick(dt); // buy-gain animations (docs/12 #1)
      this.shop.handleInput(
        input.dashPressed, input.altPressed, input.trashPressed,
        input.infoPressed, moveXEdge, moveYEdge,
      );
      return true;
    }
    return false;
  }

  private render(alpha: number): void {
    // Fullscreen toggles / window resizes: refit every open overlay so the
    // dim always covers the canvas and nothing leaks from the background
    const w = this.stage.width;
    const h = this.stage.height;
    if (w !== this.lastScreenW || h !== this.lastScreenH) {
      this.lastScreenW = w;
      this.lastScreenH = h;
      if (this.fsPrompt.open) this.fsPrompt.show(w, h);
      if (this.title.open) this.title.relayout(w, h);
      if (this.settingsScreen.open) this.settingsScreen.resize(w, h);
      if (this.shopScreen.open) this.shopScreen.resize(w, h);
      if (this.gameOver.open) this.gameOver.resize(w, h);
      if (this.runEnd.open) this.runEnd.resize(w, h);
    }

    this.ambiance.update(GAME.fixedStep); // render-rate tick is fine for a slow pulse
    music.sync(); // fades out instantly when sound is toggled off
    this.hud.layout(w, h);
    this.touchUi.layout(w, h);
    // Fight controls only while the fight runs: under a dimmed overlay their
    // hidden buttons turned taps into dash (= confirm) / pause
    const overlay = this.shopScreen.open || this.title.open || this.gameOver.open
      || this.runEnd.open || this.settingsScreen.open;
    this.touchUi.setVisible(this.input.activeDevice === 'touch' && !overlay);
    this.touchUi.sync(this.world.player);

    this.world.render(alpha);
    syncHud(this.hud, this.world, this.run, this.rewards.pilotLevel, w);
    this.world.camera.apply(this.stage.world, w, h, alpha);
  }
}
